import * as React from 'react';
import { Button, Card, Typography, Alert, Input, Space, Divider } from 'antd';
import { GoogleOutlined, MailOutlined } from '@ant-design/icons';
import { signInWithGoogle, sendEmailLink } from '../firebase/auth';

const { Title, Text } = Typography;

export function Login(): React.ReactElement {
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [email, setEmail] = React.useState<string>('');
    const [emailSent, setEmailSent] = React.useState<boolean>(false);

    const handleGoogleSignIn = async () => {
        try {
            setLoading(true);
            setError(null);
            await signInWithGoogle();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to sign in');
            console.error('Sign in error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSendEmailLink = async () => {
        if (!email || !email.includes('@')) {
            setError('Please enter a valid email address');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            // Include the admin URL in the redirect
            const continueUrl = `${window.location.origin}/admin`;
            await sendEmailLink(email, continueUrl);
            setEmailSent(true);
        } catch (err: any) {
            const errorMessage = err?.code === 'auth/configuration-not-found' 
                ? 'Email link authentication is not configured. Please contact support.'
                : err?.message || 'Failed to send email link';
            setError(errorMessage);
            console.error('Send email link error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '100vh',
            background: '#ffffff'
        }}>
            <Card 
                style={{ 
                    width: 400, 
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    borderRadius: '16px'
                }}
            >
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <Title level={2}>Admin Login</Title>
                    <Text type="secondary">Sign in to access the admin panel</Text>
                </div>
                
                {error && (
                    <Alert 
                        message="Error" 
                        description={error} 
                        type="error" 
                        showIcon 
                        style={{ marginBottom: '16px' }}
                    />
                )}

                {emailSent ? (
                    <Alert
                        message="Check your email"
                        description="We've sent you a sign-in link. Please check your email and click the link to sign in."
                        type="success"
                        showIcon
                        style={{ marginBottom: '16px' }}
                    />
                ) : (
                    <>
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Button
                                type="primary"
                                icon={<GoogleOutlined />}
                                size="large"
                                block
                                onClick={handleGoogleSignIn}
                                loading={loading}
                                style={{ height: '48px', fontSize: '16px' }}
                            >
                                Sign in with Google
                            </Button>

                            <Divider>or</Divider>

                            <Input
                                type="email"
                                placeholder="Enter your email"
                                prefix={<MailOutlined />}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onPressEnter={handleSendEmailLink}
                                size="large"
                                style={{ height: '48px' }}
                            />

                            <Button
                                icon={<MailOutlined />}
                                size="large"
                                block
                                onClick={handleSendEmailLink}
                                loading={loading}
                                style={{ height: '48px', fontSize: '16px' }}
                            >
                                Send sign-in link
                            </Button>
                        </Space>
                    </>
                )}
            </Card>
        </div>
    );
}

